/**
 * Browser-side helpers for the Mailchimp Connected Sites tag.
 *
 * The Connected Sites script exposes a global function (usually `mc`)
 * that lets us identify the current visitor and trigger events that
 * Mailchimp can then use for behavioral targeting. The exact global
 * varies between accounts, so this module feature-detects safely and
 * falls back to GTM's `dataLayer` so events are never lost.
 */

type MailchimpFn = (action: string, payload?: Record<string, unknown>) => void;
/*
declare global {
  interface Window {
    mc?: MailchimpFn;
    _mcTracker?: MailchimpFn;
    dataLayer?: Array<Record<string, unknown>>;
  }
}
*/

function getMailchimpFn(): MailchimpFn | undefined {
  if (typeof window === "undefined") return undefined;
  if (typeof window.mc === "function") return window.mc;
  if (typeof window._mcTracker === "function") return window._mcTracker;
  return undefined;
}

export function isMailchimpReady(): boolean {
  return !!getMailchimpFn();
}

/**
 * Identify the current visitor to Mailchimp. Safe to call multiple
 * times; the tag de-dupes by email server-side.
 */
export function mailchimpIdentify(
  email: string,
  attributes: Record<string, string | number | boolean> = {},
): void {
  if (!email || typeof window === "undefined") return;
  const fn = getMailchimpFn();
  if (fn) {
    try {
      fn("identify", { email, ...attributes });
      return;
    } catch {
      // fall through to dataLayer
    }
  }
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: "mailchimp_identify",
      mc_email: email,
      mc_attributes: attributes,
    });
  }
}

/**
 * Fire a custom Mailchimp event. Mailchimp's Connected Sites tag
 * accepts plain string event names with primitive properties.
 */
export function mailchimpTrack(
  name: string,
  properties: Record<string, string | number | boolean> = {},
): void {
  if (!name || typeof window === "undefined") return;
  const fn = getMailchimpFn();
  if (fn) {
    try {
      fn("track", { name, properties });
      return;
    } catch {
      // fall through to dataLayer
    }
  }
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: "mailchimp_event",
      mc_event_name: name,
      mc_event_properties: properties,
    });
  }
}
